import type { Content } from 'mdast';

/**
 * Chapter side-panel graphs for the print scenario.
 *
 * The live site shows the existing `PlaybookExtrapolatedGraph` (capability over
 * time + loss-of-control gradient) in its HUD, frozen to the reader's date. For
 * print we want that SAME component, one per chapter, floated into the gutter.
 *
 * It's a real React component (with a canvas layer), so it can't be serialized
 * to an html string here. Instead we inject an empty floated SLOT at each
 * `:::dateMarker`, carrying the date; PrintScenario then client-mounts the
 * actual graph into each slot (see its useEffect). This module only does the
 * AST surgery that places the slots in the right spots in the flow.
 */

function dateMarkerDate(node: Content): string | null {
  const n = node as { type?: string; name?: string; children?: unknown[] };
  if (n.type !== 'containerDirective' || n.name !== 'dateMarker') return null;
  const text = (n.children ?? [])
    .flatMap((c) => (c as { children?: { value?: string }[] }).children ?? [])
    .map((c) => c.value ?? '')
    .join('\n');
  const m = text.match(/\d{4}-\d{2}-\d{2}/);
  return m ? m[0] : null;
}

export function injectGraphSlots(
  nodes: Content[],
  maxYear: number,
  // Bound-book layout (?book=1): also emit a left-gutter copy of each slot —
  // the page face's parity decides which copy is on the paper (print.css
  // "Bound-book layout"). Off for plain prints so the panels aren't mounted
  // twice for nothing.
  dualGutter = false,
): Content[] {
  // The HUD data is per-year, so one panel per year — skip extra mid-year
  // dateMarkers (e.g. 2027-06-01) that would render a redundant duplicate, and
  // skip years past the data range (the far-future epilogue, where getDataForDate
  // would just repeat the last phase's numbers).
  const seenYears = new Set<string>();
  // The epilogue gets no panels at all — everything after its separator page
  // (injected as raw HTML by scenario/page.tsx) only drops its markers.
  let inEpilogue = false;
  const walk = (list: Content[]): Content[] =>
    list.flatMap((node) => {
      const html = node as { type?: string; value?: string };
      if (
        html.type === 'html' &&
        html.value?.includes('print-epilogue-divider')
      ) {
        inEpilogue = true;
      }
      const date = dateMarkerDate(node);
      if (date) {
        const year = date.slice(0, 4);
        if (!inEpilogue && !seenYears.has(year) && Number(year) <= maxYear) {
          seenYears.add(year);
          return [
            {
              type: 'html',
              value:
                `<div class="print-graph-slot" data-graph-date="${date}"></div>` +
                (dualGutter
                  ? `<div class="print-graph-slot print-graph-slot-left" data-graph-date="${date}"></div>`
                  : ''),
            } as unknown as Content,
          ];
        }
        // Redundant same-year marker: REMOVE it from the tree. An empty html
        // node here isn't harmless — the renderer wraps it in an (empty) div,
        // and when the marker sits between a heading and its first paragraph
        // that div defeats the heading's break-after: avoid: Chromium keeps
        // the heading with the invisible div, then breaks after it — the
        // orphaned year heading at the foot of a page.
        return [];
      }
      const children = (node as { children?: Content[] }).children;
      if (Array.isArray(children)) {
        return [{ ...node, children: walk(children) } as Content];
      }
      return [node];
    });
  return walk(nodes);
}

/**
 * For chapters that get NO side panels (the alternative-plan appendices,
 * whose HUD data would be the main plan's), dateMarkers must still be kept
 * out of the printed flow. With maxYear -Infinity every marker takes the
 * "drop it" branch above.
 */
export function stripDateMarkers(nodes: Content[]): Content[] {
  return injectGraphSlots(nodes, Number.NEGATIVE_INFINITY);
}
